import { api_configuration } from "../../common";
import api_report = api_configuration.report_protocol_types;
import api_gui_model = api_configuration.libraries.gui_definition_lib.types.gui_model.alan_api;
import api_views = api_configuration.libraries.gui_definition_lib.types.views.alan_api;
import api_client_request = api_configuration.libraries.gui_definition_lib.libraries.model_lib.types.client_request;
import api_client_state = api_configuration.types.client_state.alan_api;
import client_request_decorator = api_configuration.libraries.gui_definition_lib.libraries.model_lib.types.client_request.alan_api;
import client_query_to_excel_transformation = api_configuration.report_protocol_types.collection_query_to_table;
import api_download_request = api_report.report_protocol_download_request;
import createCollectionQueryFromQuery from "./createCollectionQueryFromQuery";
import { CollectionPathBuilder } from "../navigation/createCollectionPathBuilder";
import escape = require("./createEscapedCsvHeader");
import { settings, client_state } from "../state/ClientState";
import { createIdPathBuilderFromNode } from "../navigation/createIdPathFromNode";
import NodePathBuilder from "../navigation/NodePathBuilder";
import { mapNodePath, createFiltersFromColumns } from "./createPropertiesDictionaryFromColumns";
import * as createCollectionPathBuilderFromCollectionPath from "./createCollectionPathBuilderFromCollectionPath";
import { identity, select } from "../validation";
import { isKeyProperty } from "../state/isKey";
import { DatastoreProxy } from "../interface/DatastoreProxy";
import { CsvColumnPathBuilder } from "../navigation/CsvColumnPathBuilder";
import { orderedAttributes } from "../graphIterators";
import { getStaticDecimalsFromNumericalType } from "./serializeNumber";
import { IdPathBuilder } from "../navigation/IdPathBuilder";

function textExpression(static_text: string): api_client_request.alan_api.Tproperties__node_query {
	return {
		path: {
			"has steps": ["no", {}],
		},
		type: [
			"text",
			{
				expression: { type: ["singular", { expression: { type: ["static", { string: static_text }] } }] },
			},
		],
	};
}

function createSimpleColumnNameFromNodePath(
	node_path: api_views.Cnode_path,
	onText?: () => string,
	onReference?: (reference: api_gui_model.Cyes__navigable) => string
): string {
	return node_path.properties.has_steps.switch({
		no: function () {
			return onText ? onText() : "";
		},
		yes: function (yes) {
			const tail = createSimpleColumnNameFromNodePath(yes.properties.tail, onText, onReference);
			return yes.properties.type.switch({
				parent: tail,
				"state rule": tail,
				"reference rule": function (reference) {
					if (onReference) {
						return onReference(reference.properties.property.inferences.navigable()) + "." + tail;
					} else {
						return tail;
					}
				},
				reference: function (reference) {
					if (onReference) {
						return onReference(reference.properties.text.inferences.navigable()) + "." + tail;
					} else {
						return tail;
					}
				},
				group: tail,
				state: tail,
				"collection entry": tail,
			});
		},
	});
}

function clientQueryToExcelTransformation(
	query_definition: api_views.Cqueries
): client_query_to_excel_transformation.alan_api.Tcollection_query_to_table {
	const columns_block_builders: Array<
		(
			tail: client_query_to_excel_transformation.alan_api.Tcollection_block
		) => client_query_to_excel_transformation.alan_api.Tcollection_block
	> = [];

	query_definition.properties.columns.forEach(function (column) {
		column.properties.type.switch({
			text: function (text) {
				text.parent.properties.column_type.switch({
					id: undefined,
					content: function () {
						columns_block_builders.push(function (tail) {
							return {
								"has columns": [
									"yes",
									{
										header: createSimpleColumnNameFromNodePath(
											text.parent.properties.path,
											() => escape(text.properties.text.entry),
											(reference) => escape(reference.parent.parent.parent.key.entry)
										),
										property: {
											property: text.parent.key,
											type: ["text", {}],
										},
										next: tail,
									},
								],
							};
						});
					},
				});
			},
			number: function (number) {
				columns_block_builders.push(function (tail) {
					return {
						"has columns": [
							"yes",
							{
								header: escape(number.properties.number.ref.inferences.number().parent.parent.key),
								property: {
									property: number.parent.key,
									type: number.properties.number.ref.properties.numerical_type.ref.properties.representation_type.switch<
										client_query_to_excel_transformation.alan_api.Tproperty_selection["type"]
									>({
										model: [
											"decimal",
											{
												shift: -getStaticDecimalsFromNumericalType(
													//notice the minus sign, because it is a negative shift
													number.properties.number.ref.inferences.number().properties.numerical_type.ref
												),
											},
										],
										date: ["date", {}],
										"date and time": ["datetime", {}],
										"HTML date and time": ["datetime", {}],
										duration: ["number", {}],
									}),
								},
								next: tail,
							},
						],
					};
				});
			},
			file: undefined, // files are not supported
			"state group": function (state_group) {
				columns_block_builders.push(function (tail): client_query_to_excel_transformation.alan_api.Tcollection_block {
					return {
						"has columns": [
							"yes",
							{
								header: escape(state_group.properties.state_group.entry),
								property: {
									property: state_group.parent.key,
									type: ["state", {}],
								},
								next: tail,
							},
						],
					};
				});
			},
			widget: undefined,
		});
	});
	return {
		type: [
			"collection query",
			{
				columns: columns_block_builders
					.reverse()
					.reduce<client_query_to_excel_transformation.alan_api.Tcollection_block>(
						(previous_step, current_builder) => current_builder(previous_step),
						{ "has columns": ["no", {}] }
					),
			},
		],
	};
}

type ImportTemplateColumn = {
	column_name: string;
	query_property: client_request_decorator.Tproperties__node_query;
	transformation: (
		tail: client_query_to_excel_transformation.alan_api.Tcollection_block
	) => client_query_to_excel_transformation.alan_api.Tcollection_block;
};

function createImportTemplateQueryColumns(
	node: api_gui_model.Cgui_node,
	path_builder: NodePathBuilder,
	column_path_builder: CsvColumnPathBuilder,
	state_group_operation: "update" | "set|update",
	collection_key_name: string
): ImportTemplateColumn[] {
	return orderedAttributes(node.input.application_node())
		.map((application_attribute) => {
			const attribute = node.properties.attributes.get(application_attribute.key)!;
			return attribute.properties.type.switch({
				command: [],
				action: [],
				property: ($) =>
					$.properties.type.switch<ImportTemplateColumn[]>({
						collection: () => [],
						file: () => [],
						group: ($) =>
							createImportTemplateQueryColumns(
								$.properties.gui_node,
								path_builder.group(attribute.key.entry),
								column_path_builder.group(attribute.key.entry),
								state_group_operation,
								collection_key_name
							),
						number: ($) =>
							$.inferences.number().properties.type.switch({
								derived: [],
								base: (elementary_number) =>
									elementary_number.properties.type.switch({
										causal: [],
										simple: () => {
											const number_header = column_path_builder.number(attribute.key.entry).build();
											const number_properties: ImportTemplateColumn[] = [
												{
													column_name: number_header,
													query_property: {
														path: path_builder.build(),
														type: [
															"number",
															{
																expression: {
																	type: [
																		"number property",
																		{
																			"signed number property": {
																				path: new NodePathBuilder().build(),
																				sign: ["positive", {}],
																				"property name": attribute.key.entry,
																			},
																		},
																	],
																},
															},
														],
													},
													transformation: (tail) =>
														/* The number conversion is done using client transformations. So the
											 "type" is always "number". */
														({
															"has columns": [
																"yes",
																{
																	header: number_header,
																	property: {
																		property: number_header,
																		type: ["number", {}],
																	},
																	next: tail,
																},
															],
														}),
												},
											];
											return number_properties;
										},
									}),
							}),
						"state group": ($) =>
							$.inferences.state_group().properties.type.switch({
								derived: [],
								base: () => {
									const state_group_operation_header = column_path_builder
										.state_group_operation(attribute.key.entry)
										.build();
									const state_group_header = column_path_builder.state_group(attribute.key.entry).build();
									const state_group_properties: ImportTemplateColumn[] = [
										{
											column_name: state_group_header,
											query_property: {
												path: path_builder.build(),
												type: [
													"state group",
													{
														"state group": attribute.key.entry,
													},
												],
											},
											transformation: (tail) => ({
												"has columns": [
													"yes",
													{
														header: state_group_header,
														property: {
															property: state_group_header,
															type: ["state", {}],
														},
														next: tail,
													},
												],
											}),
										},
										{
											column_name: state_group_operation_header,
											query_property: textExpression(state_group_operation),
											transformation: (tail) => ({
												"has columns": [
													"yes",
													{
														header: state_group_operation_header,
														property: {
															property: state_group_operation_header,
															type: ["text", {}],
														},
														next: tail,
													},
												],
											}),
										},
									];
									function orderState(
										state: api_gui_model.Cstates__state_group__type__property__type__attributes
									): api_gui_model.Cstates__state_group__type__property__type__attributes[] {
										switch (state.properties.has_successor.state.name) {
											case "no": {
												return [state];
											}
											case "yes": {
												const $ = state.properties.has_successor.state.node;
												return [state].concat(orderState($.properties.successor.ref));
											}
											default:
												unexpectedState(state.properties.has_successor.state);
										}
									}
									switch ($.properties.has_states.state.name) {
										case "no": {
											return state_group_properties;
										}
										case "yes": {
											const yes = $.properties.has_states.state.node;

											return state_group_properties.concat(
												orderState(yes.properties.first.ref)
													.map((state) =>
														createImportTemplateQueryColumns(
															state.properties.gui_node,
															path_builder.state(attribute.key.entry, state.key.entry),
															column_path_builder.state(attribute.key.entry, state.key.entry),
															"set|update",
															collection_key_name
														)
													)
													.reduce((p, c) => p.concat(c), [])
											);
										}
										default:
											unexpectedState($.properties.has_states.state);
									}
								},
							}),
						text: ($) =>
							$.inferences.text().properties.type.switch({
								derived: [],
								base: () => {
									function createTextColum() {
										const text_header = select(isKeyProperty($.inferences.text()), collection_key_name, () =>
											column_path_builder.text(attribute.key.entry).build()
										);
										return identity<ImportTemplateColumn[]>([
											{
												column_name: text_header,
												transformation: (tail) => ({
													"has columns": [
														"yes",
														{
															header: text_header,
															property: {
																property: text_header,
																type: ["text", {}],
															},
															next: tail,
														},
													],
												}),
												query_property: {
													path: path_builder.build(),
													type: [
														"text",
														{
															expression: {
																type: [
																	"singular",
																	{
																		expression: {
																			type: [
																				"dynamic",
																				{
																					path: {
																						"has steps": ["no", {}],
																					},
																					text: attribute.key.entry,
																				},
																			],
																		},
																	},
																],
															},
														},
													],
												},
											},
										]);
									}
									return $.properties.navigable.switch<ImportTemplateColumn[]>({
										no: ($) =>
											$.properties.password_property.switch({
												no: () => createTextColum(),
												yes: [],
											}),
										yes: () => createTextColum(),
									});
								},
							}),
					}),
				"reference set": [],
			});
		})
		.reduce((p, c) => p.concat(c), []);
}

function createHeaderFields(collection_path: api_views.Cquery_path): string[] {
	return collection_path.properties.has_steps.switch<string[]>({
		no: [],
		yes: ($) => [$.properties.collection.entry].concat(createHeaderFields($.properties.tail)),
	});
}

export function forImportTemplate(query: api_client_state.Cquery__use_for__query): {
	query: client_request_decorator.Tcollection_query;
	transformation: client_query_to_excel_transformation.alan_api.Ccollection_query_to_table;
} {
	const item_operation: ImportTemplateColumn[] = [
		{
			column_name: "item_operation",
			query_property: textExpression("update"),
			transformation: (tail) => ({
				"has columns": [
					"yes",
					{
						header: "item_operation",
						property: {
							property: "item_operation",
							type: ["text", {}],
						},
						next: tail,
					},
				],
			}),
		},
	];
	const collection_key_name = createHeaderFields(query.inferences.node_context().properties.path).join(".");
	const import_template_columns = item_operation.concat(
		createImportTemplateQueryColumns(
			query.inferences.node_context().properties.path.output.result_node().output.gui_entity(),
			new NodePathBuilder(),
			CsvColumnPathBuilder.create(),
			"update",
			collection_key_name
		)
	);
	const query_properties = import_template_columns.reduce<
		NonNullable<api_client_request.alan_api.Tnode_query["properties"]>
	>((p, c) => ((p[c.column_name] = c.query_property), p), {});
	const query_filters = createFiltersFromColumns(query.parent);
	const transformation_columns = import_template_columns
		.slice(0)
		.reverse()
		.reduce<client_query_to_excel_transformation.alan_api.Tcollection_block>(
			(tail, import_template_column) => import_template_column.transformation(tail),
			{ "has columns": ["no", {}] }
		);
	const raw_client_request_incl_template: client_request_decorator.Tcollection_query = {
		"context node path": createIdPathBuilderFromNode(query.inferences.node()).build(),
		"collection path": createCollectionPathBuilderFromCollectionPath
			.create(query.inferences.node_context().properties.path, CollectionPathBuilder.create())
			.build(),
		"select entries": [
			"yes",
			{
				"limit number of entries": settings.properties.settings.properties.report_limit.value,
				properties: {
					properties: query_properties,
				},
			},
		],
		filters: {
			"todo filter": query.parent.properties.definition.ref.properties.filters.properties.todo_filter.switch<
				client_request_decorator.Tfilters["todo filter"]
			>({
				no: ["no", {}],
				yes: ($) => [
					"yes",
					{
						path: mapNodePath($.properties.path).build(),
					},
				],
			}),
			"value filters": query_filters,
		},
		aggregates: {},
	};

	return {
		query: raw_client_request_incl_template,
		transformation: client_query_to_excel_transformation.alan_api.Ccollection_query_to_table.create(
			{
				type: [
					"collection query",
					{
						columns: transformation_columns,
					},
				],
			},
			{
				request: api_client_request.alan_api.Cclient_request.create(
					{ type: ["collection query", raw_client_request_incl_template] },
					{
						application: DatastoreProxy.instance.application_model,
					}
				),
			}
		),
	};
}

export function determineAccessType(): api_download_request.alan_api.Treport_protocol_download_request["access type"] {
	return client_state.properties.session.properties.status.switch<
		api_download_request.alan_api.Treport_protocol_download_request["access type"]
	>({
		"not authenticated": "anonymous",
		authenticated: ($) =>
			$.properties.anonymous_session.switch<
				api_download_request.alan_api.Treport_protocol_download_request["access type"]
			>({
				yes: "anonymous",
				no: [
					"authenticated",
					{
						token: $.properties.token,
					},
				],
			}),
	});
}

export function fromQuery(
	query: api_client_state.Cquery__use_for__query
): api_download_request.alan_api.Creport_protocol_download_request {
	const collection_query = client_request_decorator.Cclient_request.create(
		{
			type: [
				"collection query",
				createCollectionQueryFromQuery(
					query.inferences.node_context().properties.switch_.switch({
						current: () => createIdPathBuilderFromNode(query.inferences.node()),
						root: () => IdPathBuilder.create(),
					}),
					query.parent,
					settings.properties.settings.properties.report_limit.value
				),
			],
		},
		{
			application: DatastoreProxy.instance.application_model,
		}
	);
	const transformation = client_query_to_excel_transformation.alan_api.Ccollection_query_to_table.create(
		clientQueryToExcelTransformation(query.parent.properties.definition.ref),
		{
			request: collection_query,
		}
	);
	return api_download_request.alan_api.Creport_protocol_download_request.create({
		"access type": determineAccessType(),
		type: [
			"ad hoc",
			{
				query: JSON.stringify(api_client_request.serializer_json.serialize(collection_query)),
				mapping: JSON.stringify(client_query_to_excel_transformation.serializer_json.serialize(transformation)),
			},
		],
	});
}
